import React, { useState } from 'react';
import { Table, Form, Badge, Pagination, Button, Modal, Spinner } from 'react-bootstrap';
import axios from 'axios';

const PendingApplicantsTable = ({
  applicants,
  selectedApplicants,
  onSelectApplicant,
  onSelectAll,
  onSelectAllFiltered,
  page,
  totalPages,
  total,
  onPageChange,
  onSendReminders,
  allFilteredCount
}) => {
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedApplicantHistory, setSelectedApplicantHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const fetchEmailHistory = async (atsNumber) => {
    try {
      setLoadingHistory(true);
      setShowHistoryModal(true);
      const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
      const response = await axios.get(
        `${base}/api/applicants/notifications/email-history/${atsNumber}`,
        { withCredentials: true }
      );
      setSelectedApplicantHistory(response.data);
    } catch (error) {
      console.error('Error fetching email history:', error);
      setSelectedApplicantHistory(null);
    } finally {
      setLoadingHistory(false);
    }
  };

  const renderPaginationItems = () => {
    const items = [];
    const startPage = Math.max(2, page - 2);
    const endPage = Math.min(totalPages - 1, page + 2);

    items.push(
      <Pagination.Item key={1} active={page === 1} onClick={() => onPageChange(1)}>
        1
      </Pagination.Item>
    );

    if (startPage > 2) {
      items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item key={i} active={page === i} onClick={() => onPageChange(i)}>
          {i}
        </Pagination.Item>
      );
    }

    if (endPage < totalPages - 1) {
      items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
    }

    if (totalPages > 1) {
      items.push(
        <Pagination.Item key={totalPages} active={page === totalPages} onClick={() => onPageChange(totalPages)}>
          {totalPages}
        </Pagination.Item>
      );
    }

    return items;
  };

  const getStatusBadge = (emailSentCount, emailSkipCount) => {
    if (emailSkipCount > 0) {
      return <Badge bg="danger">Invalid Email</Badge>;
    }
    if (emailSentCount > 0) {
      return <Badge bg="info">Contacted ({emailSentCount}x)</Badge>;
    }
    return <Badge bg="warning">Needs Follow-up</Badge>;
  };

  return (
    <div>
      <style>{`
        .email-log-btn {
          padding: 0.25rem 0.5rem;
          font-size: 0.75rem;
          border-radius: 4px;
          border: 1px solid #6366f1;
          background: transparent;
          color: #6366f1;
          cursor: pointer;
          transition: all 0.2s;
        }
        .email-log-btn:hover {
          background: #6366f1;
          color: white;
        }
        .history-timeline {
          max-height: 400px;
          overflow-y: auto;
          padding: 1rem;
          background: #f9fafb;
          border-radius: 8px;
        }
        .history-item {
          display: flex;
          gap: 1rem;
          margin-bottom: 1.5rem;
          position: relative;
        }
        .history-item::before {
          content: '';
          position: absolute;
          left: 11px;
          top: 30px;
          bottom: -20px;
          width: 2px;
          background: #e5e7eb;
        }
        .history-item:last-child::before {
          display: none;
        }
        .history-icon {
          flex-shrink: 0;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.75rem;
          z-index: 1;
        }
        .history-icon.success {
          background: #10b981;
          color: white;
        }
        .history-icon.failed {
          background: #ef4444;
          color: white;
        }
        .history-icon.skipped {
          background: #f59e0b;
          color: white;
        }
        .history-content {
          flex: 1;
          background: white;
          padding: 0.75rem;
          border-radius: 6px;
          border: 1px solid #e5e7eb;
        }
      `}</style>

      <div className="d-flex justify-content-between align-items-center mb-2">
        <h5>
          Pending Applicants (Page {page} of {totalPages}, Total: {total})
          {allFilteredCount > total && (
            <span className="text-muted ms-2">
              ({allFilteredCount} total filtered)
            </span>
          )}
        </h5>
        <div className="d-flex gap-2">
          {allFilteredCount > total && (
            <Button
              variant="success"
              size="sm"
              onClick={onSelectAllFiltered}
              disabled={selectedApplicants.length === allFilteredCount}
            >
              Select All Filtered ({allFilteredCount})
            </Button>
          )}
          <Button
            variant="primary"
            onClick={onSendReminders}
            disabled={selectedApplicants.length === 0}
          >
            Send Reminders ({selectedApplicants.length})
          </Button>
        </div>
      </div>
      <Table responsive striped hover>
        <thead>
          <tr>
            <th>
              <Form.Check
                type="checkbox"
                checked={applicants.length > 0 && applicants.every(a => selectedApplicants.includes(a.ats_number))}
                onChange={e => onSelectAll(e.target.checked)}
              />
            </th>
            <th>ATS Number</th>
            <th>Name</th>
            <th>Email</th>
            <th>Domain</th>
            <th>Application Date</th>
            <th>Status</th>
            <th>Email Log</th>
          </tr>
        </thead>
        <tbody>
          {applicants.map(applicant => (
            <tr key={applicant.ats_number}>
              <td>
                <Form.Check
                  type="checkbox"
                  checked={selectedApplicants.includes(applicant.ats_number)}
                  onChange={e => onSelectApplicant(applicant.ats_number, e.target.checked)}
                />
              </td>
              <td><strong>{applicant.ats_number}</strong></td>
              <td>{applicant.first_name} {applicant.last_name}</td>
              <td>
                <div>{applicant.email_id}</div>
                {applicant.last_email_sent_at && (
                  <small className="text-muted">
                    Last sent: {new Date(applicant.last_email_sent_at).toLocaleDateString()}
                  </small>
                )}
              </td>
              <td>{applicant.domain || 'N/A'}</td>
              <td>{applicant.application_date || 'N/A'}</td>
              <td>
                {getStatusBadge(applicant.email_sent_count || 0, applicant.email_skip_count || 0)}
              </td>
              <td>
                <div className="d-flex flex-column gap-1 align-items-start">
                  <div>
                    {applicant.email_sent_count > 0 && (
                      <Badge bg="success" className="me-1">
                        <i className="bi bi-send-check me-1"></i>
                        {applicant.email_sent_count}
                      </Badge>
                    )}
                    {applicant.email_skip_count > 0 && (
                      <Badge bg="warning">
                        <i className="bi bi-x-circle me-1"></i>
                        {applicant.email_skip_count}
                      </Badge>
                    )}
                    {applicant.email_sent_count === 0 && applicant.email_skip_count === 0 && (
                      <Badge bg="secondary">No activity</Badge>
                    )}
                  </div>
                  {(applicant.email_sent_count > 0 || applicant.email_skip_count > 0) && (
                    <button
                      className="email-log-btn"
                      onClick={() => fetchEmailHistory(applicant.ats_number)}
                    >
                      <i className="bi bi-clock-history me-1"></i>
                      View History
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <div className="d-flex justify-content-center mt-3">
        <Pagination>
          <Pagination.First onClick={() => onPageChange(1)} disabled={page === 1} />
          <Pagination.Prev onClick={() => onPageChange(page - 1)} disabled={page === 1} />
          {renderPaginationItems()}
          <Pagination.Next onClick={() => onPageChange(page + 1)} disabled={page === totalPages} />
          <Pagination.Last onClick={() => onPageChange(totalPages)} disabled={page === totalPages} />
        </Pagination>
      </div>

      {/* Email History Modal */}
      <Modal show={showHistoryModal} onHide={() => setShowHistoryModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-clock-history me-2"></i>
            Email History
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingHistory ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">Loading email history...</p>
            </div>
          ) : selectedApplicantHistory ? (
            <>
              <div className="mb-4 p-3 bg-light rounded">
                <h6 className="mb-2">{selectedApplicantHistory.applicant.name}</h6>
                <div className="d-flex gap-3 flex-wrap">
                  <div>
                    <small className="text-muted">ATS Number:</small>
                    <div><strong>{selectedApplicantHistory.applicant.ats_number}</strong></div>
                  </div>
                  <div>
                    <small className="text-muted">Email:</small>
                    <div>{selectedApplicantHistory.applicant.email}</div>
                  </div>
                  <div>
                    <small className="text-muted">Emails Sent:</small>
                    <div>
                      <Badge bg="success">
                        {selectedApplicantHistory.applicant.email_sent_count || 0}
                      </Badge>
                    </div>
                  </div>
                  <div>
                    <small className="text-muted">Emails Skipped:</small>
                    <div>
                      <Badge bg="warning">
                        {selectedApplicantHistory.applicant.email_skip_count || 0}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>

              {selectedApplicantHistory.history && selectedApplicantHistory.history.length > 0 ? (
                <div className="history-timeline">
                  {selectedApplicantHistory.history.map((event, index) => (
                    <div key={index} className="history-item">
                      <div className={`history-icon ${event.status}`}>
                        {event.status === 'success' && <i className="bi bi-check"></i>}
                        {event.status === 'failed' && <i className="bi bi-x"></i>}
                        {event.status === 'skipped' && <i className="bi bi-exclamation"></i>}
                      </div>
                      <div className="history-content">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div>
                            <Badge bg={
                              event.status === 'success' ? 'success' :
                              event.status === 'failed' ? 'danger' : 'warning'
                            }>
                              {event.status.toUpperCase()}
                            </Badge>
                            <Badge bg="secondary" className="ms-2">
                              {event.sent_via || 'unknown'}
                            </Badge>
                          </div>
                          <small className="text-muted">
                            {new Date(event.sent_at).toLocaleString()}
                          </small>
                        </div>
                        {event.subject && (
                          <div className="mb-1">
                            <small className="text-muted">Subject:</small>
                            <div style={{ fontSize: '0.9rem' }}>{event.subject}</div>
                          </div>
                        )}
                        {event.reason && (
                          <div className="text-warning">
                            <small><i className="bi bi-info-circle me-1"></i>{event.reason}</small>
                          </div>
                        )}
                        {event.error && (
                          <div className="text-danger">
                            <small><i className="bi bi-exclamation-triangle me-1"></i>{event.error}</small>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-muted">
                  <i className="bi bi-inbox" style={{ fontSize: '3rem' }}></i>
                  <p className="mt-2">No email history available</p>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-4 text-muted">
              <p>Failed to load email history</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowHistoryModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PendingApplicantsTable;